
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../../shared/Elements";
import { BaseService, ITournamentEditionRegistered, ITournamentEditionMatch, ITournamentEditionMatchResult, ITournamentEditionRegisteredParticipant, SanitizedResponse, TournamentMatchEndScore, TournamentMatchStatus, TournamentService, TournamentStatus, ITournamentEditionScoring, TournamentEditionScoringType, ConsusService, IUploadedFile, ITournamentEditionMatchResultProof } from "../../../../../services";
import PageDesign from "../../../../page_design/PageDesign";
import { Button, ButtonManageRef, Checkbox, Classname, Column, DOMHelper, Dropdown, FileHelper, FileInputManageRef, FileInputPreviewType, FormControl, NoseurObject, NumberInput, ObjectHelper, Scheme, ScrollPanel, Table, TabPane, TabPanel, TextAreaInput, TextInput, useSubscription } from "@rivtn/noseur";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import { Schemes, Utils } from "../../../../../utils";
import { computeMatchVariables, matchStatusColorMap, onScreenshot, showTournamentPass } from "./MatchCard";
import MediaInput from "../../../../shared/MediaInput";
import Result, { getProofFileType, previewProof } from "./Result";
import ResultProof from "./ResultProof";

export interface MatchProps {
    action?: string;
    tournamentName: string;
    reloadMatch: () => void;
    match: ITournamentEditionMatch;
    tournamentEditionRegisteredParticipantId: string;
}

function Match(props: MatchProps) {
    const consusService = ConsusService.getInstance();
    const tournamentService = TournamentService.getInstance();
    const activeDialogMutableRoot = React.useRef<ReactDOM.Root>(null);
    const unselectMatch = useSubscription("OMNEHOST.YOUR_TOUR.MATCH.UNSELECT");
    const labels = i18nManager.Labels.tournaments.your_tournament.matches_and_reporting;
    const previewSubmissionFilePreviewManageRef = React.useRef<FileInputManageRef>(null);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const matchLabels = i18nManager.Labels.tournaments.your_tournament.matches_and_reporting.match;
    const { match, tournamentName, tournamentEditionRegisteredParticipantId, reloadMatch } = props;
    const [view, setView] = React.useState<"LINEUP" | "RESULTS">(props.action === "vr" ? "RESULTS" : "LINEUP");
    let [tournamentCustomScorings, setTournamentCustomScorings] = React.useState<ITournamentEditionScoring[]>([]);
    const {
        me, opponents, opponentsScore, thereIsNoOpponent, isVersus, isMultiVersus, upcoming, ended, ongoing, endedScoreLabel, statusLabel, myDisplay, opponentsDisplay
    } = computeMatchVariables(match, tournamentEditionRegisteredParticipantId);
    const isHomeMatch = (me as any).__isHomeMatch;
    const meLineup = buildLineup(me);
    const opponentsLineups = ((isVersus || isMultiVersus)
        ? ((isVersus) ? buildLineup(opponents[0]) : (<>
            {opponents.map((opps) => buildLineup(opps, true))}
        </>))
        : undefined);
    const myAnal = (<div>
        <span><span className="t">{matchLabels.won}</span> {me.tournament_edition_registered.own_matches_detail.matches_won ?? 0} {matchLabels.match}</span>
        <span><span className="t">{matchLabels.lost}</span> {me.tournament_edition_registered.own_matches_detail.matches_lost ?? 0} {matchLabels.match}</span>
        <span><span className="t">{matchLabels.draw}</span> {me.tournament_edition_registered.own_matches_detail.matches_drawn ?? 0} {matchLabels.match}</span>
    </div>);
    const oppsAnal = (<div>
        <span>{opponents.reduce((acc, opps) => (acc + (opps.tournament_edition_registered.own_matches_detail.matches_won ?? 0)), 0)} {matchLabels.match} <span className="t">{matchLabels.won}</span></span>
        <span>{opponents.reduce((acc, opps) => (acc + (opps.tournament_edition_registered.own_matches_detail.matches_lost ?? 0)), 0)} {matchLabels.match} <span className="t">{matchLabels.lost}</span></span>
        <span>{opponents.reduce((acc, opps) => (acc + (opps.tournament_edition_registered.own_matches_detail.matches_drawn ?? 0)), 0)} {matchLabels.match} <span className="t">{matchLabels.draw}</span></span>
    </div>);
    const selfSubmittedResults: ITournamentEditionMatchResult[] = Object.entries(match.tournament_edition_registered_participant_submitted_results?.[tournamentEditionRegisteredParticipantId] ?? {}).reduce((acc, [scoringId, submittedResults]) => {
        submittedResults.forEach((t) => {
            t.submitter_id = tournamentEditionRegisteredParticipantId;
            t.tournament_edition_registered_participants = match.tournament_edition_registered_participants;
            t.tournament_scoring = tournamentCustomScorings?.find((t) => t.external_id === scoringId) ?? { external_id: scoringId } as any;
        })
        return acc.concat(submittedResults);
    }, [] as ITournamentEditionMatchResult[]).reverse();
    const opponentsSubmittedResults: ITournamentEditionMatchResult[] = Object.entries(Object.entries(match.tournament_edition_registered_participant_submitted_results).reduce((acc, [k, v]) => {
        if (k === tournamentEditionRegisteredParticipantId) return acc;
        acc[k] = v;
        return acc;
    }, {} as NoseurObject<NoseurObject<ITournamentEditionMatchResult[]>>)).reduce((acc, [pid, opsSubmittedResults]) => {
        Object.entries(opsSubmittedResults).forEach(([scoringId, submittedResults]) => {
            submittedResults.forEach((t) => {
                t.submitter_id = pid;
                t.tournament_edition_registered_participants = match.tournament_edition_registered_participants;
                t.tournament_scoring = tournamentCustomScorings?.find((t) => t.external_id === scoringId) ?? { external_id: scoringId } as any;
            });
            acc = acc.concat(submittedResults);
        });
        return acc;
    }, [] as ITournamentEditionMatchResult[]).reverse();
    const leftSubmittedResults = (isHomeMatch ? selfSubmittedResults : opponentsSubmittedResults);
    const rightSubmittedResults = (isHomeMatch ? opponentsSubmittedResults : selfSubmittedResults);

    React.useEffect(() => {
        tournamentService.queryTournamentEditionScorings(match.tournament_edition.external_id!, {

        }).then(({ content }: SanitizedResponse<ITournamentEditionScoring>) => {
            tournamentCustomScorings = content;
            setTournamentCustomScorings(content);
            if (ongoing && props.action === "sr") {
                submitResult();
                Utils.updateSearchParams("a", undefined, setSearchParams);
            }
        }).catch(BaseService.reportError);
    }, []);

    return (<div className="your-tour-home your-tour-mat">
        <div className="closer" onClick={() => {
            Utils.updateSearchParams("mid", undefined, setSearchParams, () => unselectMatch());
        }}>
            {matchLabels.close}
        </div>
        <div className="ma-container" style={{ marginTop: 5 }}>
            <div className="fainter" style={thereIsNoOpponent ? { justifyContent: "center" } : undefined}>
                {isHomeMatch
                    ? (<>{myDisplay} {(isMultiVersus) ? <div /> : opponentsDisplay}</>)
                    : (<>{(isMultiVersus) ? <div /> : opponentsDisplay} {myDisplay}</>)}
            </div>
            <div className="snapper" style={{ justifyContent: "space-between" }}>
                <span style={{ color: matchStatusColorMap[match.status], fontWeight: "bold", }}>{statusLabel} {endedScoreLabel}</span>
                {Elements.buildSnapper((e) => {
                    e.stopPropagation();
                    let type: "versus" | "multi_versus" | "solo" = "solo";
                    if (isVersus) type = "versus";
                    else if (isMultiVersus) type = "multi_versus";

                    onScreenshot(match, tournamentName, type, opponents, labels);
                })}
            </div>
            <span className="litem" style={{ opacity: 0.6 }}>
                {labels.group} {match.group_number} - {labels.match.match} {match.match_number}
            </span>
            <span className="litem">
                {Utils.formatToFineDate2(match.start_date)}
            </span>
            <div className="versus" style={thereIsNoOpponent ? { justifyContent: "center" } : undefined}>
                {isHomeMatch
                    ? (<>
                        <div className="comp">
                            {myDisplay}
                            {/* {(ended) && (<span className="s">{me.scores?.[match.extra_data?.determining_score]}{labels.score_labels[match.scores[match.extra_data?.determining_score]]}</span>)} */}
                        </div>
                        {(isVersus || isMultiVersus) && (<>
                            <span className="vs">{ended ? "-" : labels.vs}</span>
                            <div className="comp">
                                {/* {(ended) && (<span className="s">{opponentsScore}{labels.score_labels[match.scores[match.extra_data?.determining_score]]}</span>)} */}
                                {opponentsDisplay}
                            </div>
                        </>)}
                    </>)
                    : (<>
                        {(isVersus || isMultiVersus) && (<>
                            <div className="comp">
                                {opponentsDisplay}
                                {/* {(ended) && (<span className="s">{opponentsScore}{labels.score_labels[match.scores[match.extra_data?.determining_score]]}</span>)} */}
                            </div>
                            <span className="vs">{ended ? "-" : labels.vs}</span>
                        </>)}
                        <div className="comp">
                            {/* {(ended) && (<span className="s">{me.scores?.[match.extra_data?.determining_score]}{labels.score_labels[match.scores[match.extra_data?.determining_score]]}</span>)} */}
                            {myDisplay}
                        </div>
                    </>)}
            </div>
            <div className="litem">
                {isHomeMatch
                    ? (<>
                        <ReactRouterDom.NavLink target="_blank" to={Utils.buildPublicUrl("TEAM", me.resource_id)}>{Utils.getResourceName(me.resource)}</ReactRouterDom.NavLink>
                        {(opponents.length > 0) && (<>&nbsp;<span className="vs" style={{ opacity: 0.4 }}>{ended ? "-" : labels.vs.toLowerCase()}</span>&nbsp;</>)}
                        {opponents.map((o, i) => (<React.Fragment key={Utils.getResourceName(o.resource)}>
                            {(opponents.length > 1 && i > 0) && (<>,&nbsp;</>)}
                            <ReactRouterDom.NavLink target="_blank" to={Utils.buildPublicUrl("TEAM", o.resource_id)}>{Utils.getResourceName(o.resource)}</ReactRouterDom.NavLink>
                        </React.Fragment>))}
                    </>)
                    : (<>
                        {opponents.map((o, i) => (<React.Fragment key={Utils.getResourceName(o.resource)}>
                            {(opponents.length > 1 && i > 0) && (<>,&nbsp;</>)}
                            <ReactRouterDom.NavLink target="_blank" to={Utils.buildPublicUrl("TEAM", o.resource_id)}>{Utils.getResourceName(o.resource)}</ReactRouterDom.NavLink>
                        </React.Fragment>))}
                        {(opponents.length > 0) && (<>&nbsp;<span className="vs" style={{ opacity: 0.4 }}>{ended ? "-" : labels.vs.toLowerCase()}</span>&nbsp;</>)}
                        <ReactRouterDom.NavLink target="_blank" to={Utils.buildPublicUrl("TEAM", me.resource_id)}>{Utils.getResourceName(me.resource)}</ReactRouterDom.NavLink>
                    </>)}
            </div>
            {(match.location) && (<span className="litem">
                <span style={{ opacity: 0.4 }}>{labels.location}</span>&nbsp;{match.location}
            </span>)}
        </div>
        {(view === "LINEUP") && (<>
            <div className="ma-anal">
                {(isHomeMatch) ? myAnal : (!thereIsNoOpponent ? oppsAnal : (<div />))}
                <div style={{ alignItems: "center", flexDirection: "column" }}>
                    <div style={{ alignItems: "center", flexDirection: "row" }}>
                        {(ongoing) && (<Button raised scheme={Schemes.OMNEHOST} text={labels.submit_result} style={{ padding: "10px 25px", margin: "0px 5px" }} onClick={submitResult} />)}
                        {(ongoing || ended) && (<Button raised scheme={Schemes.OMNEHOST} text={labels.view_result} style={{ padding: "10px 25px", margin: "0px 5px" }} onClick={() => {
                            Utils.updateSearchParams("a", "vr", setSearchParams, () => setView("RESULTS"));
                        }} />)}
                    </div>
                    {(upcoming || ongoing) && (<span className={"link danger"} style={{ marginTop: 10 }} onClick={forfeitMatch}>{labels.forfeit_match}</span>)}
                </div>
                {(thereIsNoOpponent) ? (<div />) : (isHomeMatch ? oppsAnal : myAnal)}
            </div>
            <div className="lineup-con" style={thereIsNoOpponent ? { justifyContent: "center" } : undefined}>
                <div className="home" style={thereIsNoOpponent ? { maxWidth: 550 } : undefined}>
                    {(isHomeMatch)
                        ? (<div className={"ma-lineup"}>
                            {meLineup}
                        </div>)
                        : (<div className={"ma-lineup not-me"}>
                            {opponentsLineups}
                        </div>)}
                </div>
                {(!thereIsNoOpponent) && (<div className="away">
                    {(isHomeMatch)
                        ? (<div className={"ma-lineup not-me away"}>
                            {opponentsLineups}
                        </div>)
                        : (<div className={"ma-lineup away"}>
                            {meLineup}
                        </div>)}
                </div>)}
            </div>
        </>)}
        {(view === "RESULTS") && (<ScrollPanel className="ma-results-con">
            <div className="closer" style={{ justifyContent: "flex-start", paddingTop: 15, width: "fit-content" }} onClick={() => {
                Utils.updateSearchParams("a", undefined, setSearchParams, () => setView("LINEUP"));
            }}>
                {matchLabels.result.back_to_lineup}
            </div>
            {(!Object.keys(match.tournament_edition_registered_participant_submitted_results ?? {})?.length) && (Elements.buildEmptyListView("fa fa-list", (<>
                <span>{matchLabels.result.empty_result_parts.no_result}</span>{" "}
                {(ongoing) && (<>
                    <span className="link" onClick={submitResult}>{matchLabels.result.empty_result_parts.submit}</span>
                    <span>{matchLabels.result.empty_result_parts.for_scoring}</span>
                </>)}
            </>)))}
            <ScrollPanel className="ma-results">
                {(leftSubmittedResults?.length > 0) && (<ScrollPanel className="own">
                    {((leftSubmittedResults).map((entry) => (<Result key={entry.date} pid={tournamentEditionRegisteredParticipantId} result={entry} />)))}
                </ScrollPanel>)}
                {(rightSubmittedResults?.length > 0) && (<ScrollPanel className="current">
                    {(rightSubmittedResults.map((entry) => (<Result key={entry.date} pid={tournamentEditionRegisteredParticipantId} result={entry} />)))}
                </ScrollPanel>)}
            </ScrollPanel>
        </ScrollPanel>)}
    </div>);

    function buildLineup(participant: ITournamentEditionRegisteredParticipant, renderTeamName?: boolean) {
        return (<React.Fragment key={Utils.getResourceName(participant.resource)}>
            <div className="snapper" style={{ justifyContent: "center" }}>
                {Elements.buildSnapper(() => {
                    if (isVersus) {
                        showTournamentPass(tournamentName, {
                            ...labels.designs_headers.lineup_versus,
                            desc: labels.designs_headers.lineup_versus.desc.replaceAll("${opponent_name}", Utils.getResourceName(opponents[0].resource))
                                .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
                        });
                    } else if (isMultiVersus) {
                        showTournamentPass(tournamentName, {
                            ...labels.designs_headers.lineup_multi_versus,
                            desc: labels.designs_headers.lineup_multi_versus.desc.replaceAll("${count}", "" + opponents.length)
                                .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
                        });
                    } else {
                        showTournamentPass(tournamentName, {
                            ...labels.designs_headers.lineup_solo,
                            desc: labels.designs_headers.lineup_solo.desc
                                .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
                        });
                    }
                })}
            </div>
            {(renderTeamName) && (<div className="header">
                <div className="mas">
                    <img className="f" alt={Utils.getResourceName(participant.resource)} src={Utils.getResourceImage(participant.resource)} />
                    <img className="i" alt={Utils.getResourceName(participant.resource)} src={Utils.getResourceImage(participant.resource)} />
                </div>
                <span className="n">{Utils.getResourceName(participant.resource)}</span>
            </div>)}
            {participant.participants?.map((mem, i) => {
                return (
                    <div key={mem.external_id ?? i} className="mem">
                        {Elements.buildSnapper(() => {
                            if (isVersus) {
                                showTournamentPass(tournamentName, {
                                    ...labels.designs_headers.lineup_member_versus,
                                    desc: labels.designs_headers.lineup_member_versus.desc.replaceAll("${opponent_name}", Utils.getResourceName(opponents[0].resource))
                                        .replaceAll("${mem}", mem.display_name ?? "Unknown")
                                        .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
                                });
                            } else if (isMultiVersus) {
                                showTournamentPass(tournamentName, {
                                    ...labels.designs_headers.lineup_member_multi_versus,
                                    desc: labels.designs_headers.lineup_member_multi_versus.desc.replaceAll("${count}", "" + opponents.length)
                                        .replaceAll("${mem}", mem.display_name ?? "Unknown")
                                        .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
                                });
                            } else {
                                showTournamentPass(tournamentName, {
                                    ...labels.designs_headers.lineup_member_solo,
                                    desc: labels.designs_headers.lineup_member_solo.desc.replaceAll("${mem}", mem.display_name ?? "Unknown")
                                        .replaceAll("${type}", (labels.designs_headers as any)[match.status.toLowerCase()]),
                                });
                            }
                        })}
                        <div className="u">
                            <img alt="desperado" src={mem.passport_picture_url} />
                            <div className="d">
                                <span className="t">{mem.display_name}</span>
                                <span className="lined-limit">{mem.profile_details}</span>
                            </div>
                        </div>
                    </div>);
            })}
        </React.Fragment>);
    }

    function submitResult() {
        if (!tournamentCustomScorings.length) return;
        const resultSubmissionPayload: NoseurObject<any> = {};
        const dialogLabels = matchLabels.dialogs.submit_request;

        let desc = dialogLabels.solo_desc;
        if (isVersus) desc = dialogLabels.versus_desc;
        else if (isMultiVersus) desc = dialogLabels.multi_versus_desc;
        desc = desc.replaceAll("${tour_name}", tournamentName)
            .replaceAll("${count}", (opponents?.length ?? "0") + "")
            .replaceAll("${opponent_name}", Utils.getResourceName(opponents?.[0]?.resource) ?? "");

        function renderTeamResultControls() {
            if (!activeDialogMutableRoot.current) return;
            const component = (<TabPane scheme={Schemes.OMNEHOST} idleScheme={Schemes.IDLE}
                attrsRelay={{ content: { style: { margin: 0, padding: "10px 1px", border: "none", borderRadius: 0 } } }}>
                {tournamentCustomScorings.map((tournamentScore) => {
                    const name = (tournamentScore.type === TournamentEditionScoringType.TOURNAMENT_SCORE ? "# " + i18nManager.Labels.host.manage_tournament.scoring.detail.tournament_score : tournamentScore.name);

                    if (!(tournamentScore.external_id in resultSubmissionPayload)) {
                        resultSubmissionPayload[tournamentScore.external_id] = {
                            tournament_edition_scoring_id: tournamentScore.external_id,
                            tournament_edition_registered_participant_id: tournamentEditionRegisteredParticipantId,
                            submitted_result: {
                                proofs: [],
                                scores: {},
                            }
                        };
                    }

                    return (<TabPanel key={tournamentScore.external_id} header={<>{name}{(tournamentScore.type === TournamentEditionScoringType.TOURNAMENT_SCORE)
                        && (<> <div style={{ width: 5, height: 5, backgroundColor: "#FF7171", margin: "auto", marginLeft: 10 }} /></>)}</>}>
                        <Table stripedRows scheme={Schemes.OMNEHOST} data={match.tournament_edition_registered_participants} totalRecords={match.tournament_edition_registered_participants.length} dataRefreshKeys={["external_id", "created_at"]}
                            style={{ flex: 1, width: "100%", marginTop: 0, overflow: "auto" }} rowsPerPage={parseInt(searchParams.get("size") ?? "100")} loadingState={Elements.LOADING_STATE} >
                            <Column className="table-c" header={"#"} template={(v) => {
                                const name = Utils.getResourceName(v.resource);
                                const imageUrl = Utils.getResourceImage(v.resource);

                                return (<div style={{ display: "flex", alignItems: "center", width: "max-content" }}>
                                    <img alt={name} src={imageUrl} style={{ width: 30, height: 30, margin: "0px 15px" }} />
                                    <span>{name} {v.external_id === tournamentEditionRegisteredParticipantId ? dialogLabels.you : ""}</span>
                                </div>);
                            }} />
                            {(Object.keys(tournamentScore.scoring).length > 0) ? (<React.Fragment key={DOMHelper.uniqueElementId("tslbo")}>
                                {Object.values(tournamentScore.scoring).filter((scoreSetting) => scoreSetting.allow_participant_submit_score).map((scoreSetting) => (<Column key={scoreSetting.id} className="table-c" header={scoreSetting.submission_name ?? scoreSetting.name} template={(participant: ITournamentEditionRegisteredParticipant) => {
                                    if (!(participant.external_id in resultSubmissionPayload[tournamentScore.external_id].submitted_result.scores)) {
                                        resultSubmissionPayload[tournamentScore.external_id].submitted_result.scores[participant.external_id] = {};
                                    }
                                    return (<NumberInput scheme={Schemes.OMNEHOST} onInputComplete={(value) => {
                                        if (!value) return;
                                        resultSubmissionPayload[tournamentScore.external_id].submitted_result.scores[participant.external_id][scoreSetting.id] = parseInt(value);
                                    }} />)
                                }} />))}
                            </React.Fragment>) : <Column />}
                        </Table>
                    </TabPanel>);
                })}
            </TabPane>);
            activeDialogMutableRoot.current.render(component);
        }

        const dialog = ConfirmDialog({
            desc,
            closable: true,
            title: dialogLabels.title,
            confirmLabel: dialogLabels.submit,
            style: { maxWidth: "90%", minWidth: "50%" },
            content: (<div style={{ margin: "10px 0px" }}>
                <div className="pms" ref={(r: any) => {
                    if (!r) return;
                    ObjectHelper.resolveRef(activeDialogMutableRoot, ReactDOM.createRoot(r));
                    renderTeamResultControls();
                }}></div>
                <div style={{ margin: "10px 0px", padding: "10px 0px", borderTop: "1px solid var(--underlineColor)" }}>
                    <MediaInput multiple concatNewFile relayToAttachFileInputOnly previewType={FileInputPreviewType.NONE} width={"100%"} height={100} instruction={dialogLabels.upload_proofs}
                        accepts="*/*" attachFileInputManageRefManageRef={previewSubmissionFilePreviewManageRef} onSelectFiles={(files) => { }} onGenerateWithAi={async () => {
                            return new File(["We are here"], "we.txt", { type: 'text/plain' });
                        }} />
                    <MediaInput multiple noControl noFillTemplate noInstruction concatNewFile stickyPreview={false} width={"100%"} height={"auto"} instruction={dialogLabels.upload_proofs}
                        fileManageRef={previewSubmissionFilePreviewManageRef} style={{ border: "none", boxShadow: "none" }}
                        onGenerateWithAi={async () => {
                            return new File(["We are here"], "we.txt", { type: 'text/plain' });
                        }} attrsRelay={{ previews: { className: "ma-proofs" } }} itemTemplate={(options) => {
                            const proof = {
                                size: options.file.size,
                                name: options.file.name,
                                location_url: options.url,
                                type: getProofFileType(options.file.type),
                            };
                            return (<ResultProof key={options.url} proof={proof} remove={() => options.onRemove(options.file)} preview={() => {
                                previewProof(proof, (_) => options.onRemove(options.file));
                            }} />);
                        }} />
                </div>
                <div style={{ marginTop: "30px 0px" }}>
                    <p style={{ color: "#FF7171" }}>{dialogLabels.note}</p>
                    <a target="_blank" rel="noreferrer" href={dialogLabels.article.location}>{dialogLabels.article.label}</a>
                </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                const uploadedProofs: ITournamentEditionMatchResultProof[] = [];
                try {
                    for (const file of (previewSubmissionFilePreviewManageRef.current?.files() ?? [])) {
                        const sanitized = (await consusService.uploadFile(`/omnehost/submitted_result/proof/${match.tournament_edition.external_id}`, `${file.name}`, file)).sanitized;
                        uploadedProofs.push({
                            name: file.name,
                            size: file.size,
                            type: getProofFileType(file.type),
                            location_url: sanitized.consus_download_url,
                            participant_name: Utils.getResourceName(me.resource),
                        });
                    }
                    const payloads = Object.values(resultSubmissionPayload);
                    for (const payload of payloads) {
                        payload.submitted_result.proofs = uploadedProofs;
                        await tournamentService.submitTournamentEditionMatchResult(match.tournament_edition.external_id, match.external_id, payload);
                        if (!(tournamentEditionRegisteredParticipantId in match.tournament_edition_registered_participant_submitted_results)) {
                            match.tournament_edition_registered_participant_submitted_results[tournamentEditionRegisteredParticipantId] = {};
                        }
                        if (!(payload.tournament_edition_scoring_id in match.tournament_edition_registered_participant_submitted_results[tournamentEditionRegisteredParticipantId])) {
                            match.tournament_edition_registered_participant_submitted_results[tournamentEditionRegisteredParticipantId][payload.tournament_edition_scoring_id] = [];
                        }
                        match.tournament_edition_registered_participant_submitted_results[tournamentEditionRegisteredParticipantId][payload.tournament_edition_scoring_id].push(payload.submitted_result);
                    }
                    setView("RESULTS");
                    Siren.alert(dialogLabels.submission_successful);
                } catch (err: any) {
                    BaseService.reportError(err);
                    consusService.deleteFile(uploadedProofs.map((p) => p.location_url)).catch(BaseService.reportError);
                }
                dialog.hide();
                button?.setLoadingState(false);
                return false;
            }
        });
        dialog.show();
    }

    function forfeitMatch() {
        let reason = "";
        const dialogErrorRef = { current: null } as any;
        const dialogLabels = labels.match.dialogs.forfeit;

        const dialog = ConfirmDialog({
            closable: true,
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            confirmLabel: dialogLabels.forfeit,
            desc: dialogLabels.desc.replaceAll("${group_number}", (match.group_number + ""))
                .replaceAll("${match_number}", (match.match_number + "")),
            content: (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
                <a href={dialogLabels.how_it_affects.location} target="_blank">{dialogLabels.how_it_affects.label}</a>
                <FormControl isFieldset fill label={dialogLabels.reason} style={{ marginTop: 15 }}>
                    <TextAreaInput scheme={Schemes.OMNEHOST} style={{ height: 150, width: "100%" }} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        reason = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ marginBottom: 10 }}> </div>
            </div>),
            onConfirm: async (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                tournamentService.forfeitTournamentEditionMatch(match.tournament_edition.external_id, match.external_id, reason, tournamentEditionRegisteredParticipantId).then(({ }: SanitizedResponse<any>) => {
                    dialog.hide(() => {
                        reloadMatch();
                        Siren.alert(dialogLabels.successfully_forfeited);
                    });
                }).catch(({ errorMessage }: any) => {
                    dialogErrorRef.current!.innerText = errorMessage;
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Match;